
'use client';

import React, { useState, useEffect } from 'react';
import { useActionState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ScanSearch } from 'lucide-react';
import { UrlInputForm } from '@/components/url-input-form';
import { AuditResultsDisplay } from '@/components/audit-results-display';
import { LoadingSpinner } from '@/components/loading-spinner';
import type { AuditState } from '@/app/actions';
import { analyzeWebsiteWithPagespeed } from '@/app/actions';
import { useToast } from "@/hooks/use-toast";
import { PackageSuggestion } from '@/components/package-suggestion';

const initialActionState: AuditState = {
  url: '',
  categories: undefined,
  error: null,
  timestamp: undefined,
  estimatedHours: undefined,
  suggestedPackageName: undefined,
  suggestionReasoning: undefined,
};

export function UrlAnalyzerTool() {
  const [formState, formAction, isPending] = useActionState<AuditState, FormData>(
    analyzeWebsiteWithPagespeed,
    initialActionState
  );
  // processedState is used to render the UI and is only updated when formState changes.
  // This helps prevent re-renders or toast spam if formState reference changes but content is same.
  const [processedState, setProcessedState] = useState<AuditState>(initialActionState);
  const [previousIsPending, setPreviousIsPending] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // When a new analysis starts (isPending becomes true)
    if (isPending && !previousIsPending) {
      setProcessedState(initialActionState); // Clear previous results from UI
    }
    setPreviousIsPending(isPending);
  }, [isPending, previousIsPending]);

  useEffect(() => {
    // This effect synchronizes the result from the server action (formState)
    // to the state used for rendering (processedState).
    // It also handles showing toasts for new errors.

    if (formState === processedState) {
      // If formState hasn't actually changed from processedState, do nothing.
      // This can happen if the component re-renders but the action hasn't returned a new state.
      return;
    }

    // Update processedState with the new state from the server action.
    setProcessedState(formState);

    // If the new formState has an error, and this error is different from
    // what was in processedState before this update, show a toast.
    // This prevents toasting the same error multiple times.
    if (formState.error && formState.error !== processedState.error) {
      toast({
        title: "Analysis Error",
        description: formState.error,
        variant: "destructive",
      });
    }
  }, [formState, processedState, toast]);

  const showAuditResults = !isPending && processedState && (processedState.url || processedState.error);
  const showPackageSuggestion = !isPending && processedState && processedState.url && !processedState.error && (processedState.suggestedPackageName || processedState.estimatedHours !== undefined);

  return (
    <section id="analyzer" className="py-12 sm:py-16 w-full scroll-mt-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <Card className="w-full max-w-4xl mx-auto shadow-xl bg-card">
          <CardHeader className="text-center">
            <ScanSearch className="h-12 w-12 text-primary mx-auto mb-4" />
            <CardTitle className="text-3xl font-headline">Website Analyzer & Package Suggester</CardTitle>
            <CardDescription>
              Enter a URL for a Lighthouse audit and get an AI-powered service package suggestion.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <UrlInputForm formAction={formAction} isPending={isPending} />
            {isPending && <LoadingSpinner />}
            {showAuditResults && (
              <AuditResultsDisplay auditState={processedState} />
            )}
            {showPackageSuggestion && (
              <div className="mt-12">
                <h3 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl font-headline text-center mb-4">
                  Our AI Recommended Package
                </h3>
                {processedState.suggestionReasoning && (
                    <p className="mt-2 mb-6 text-center text-muted-foreground">{processedState.suggestionReasoning}</p>
                )}
                <PackageSuggestion
                  suggestedPackageName={processedState.suggestedPackageName}
                  estimatedHours={processedState.estimatedHours}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
